<template>
  <span>
    <el-popover
      v-if="isPopoverMode"
      :placement="popoverPlacement"
      :trigger="popoverTrigger"
      :width="activeInteractionConfig.popoverWidth || 400"
      :popper-style="popoverStyle"
      :popper-class="popoverClass"
    >
      <template #reference>
        <span class="image-item-wrap image-item-wrap--interactive">
          <LazyImage
            class="image-item"
            :src="showSrc"
            :fit="config.fit"
            :use-lazy="config.useLazy !== false"
          />
        </span>
      </template>
      <PopoverPageRender
        :area-data="activeInteractionConfig.popoverPageData"
        :width="activeInteractionConfig.popoverWidth || 400"
      />
    </el-popover>

    <component
      v-else
      :is="safeLink ? 'a' : 'div'"
      :target="jumpWay"
      :href="safeLink || undefined"
      class="image-item-wrap"
      @click="imageClickFn"
    >
      <LazyImage
        class="image-item"
        :src="showSrc"
        :fit="config.fit"
        :use-lazy="config.useLazy !== false"
      />
    </component>

    <el-dialog
      v-if="activeInteractionConfig && activeInteractionConfig.responseType === 'popup'"
      :class="activeInteractionConfig.popupClass || ''"
      :title="activeInteractionConfig.popupTitle || ''"
      append-to-body
      v-model="dialogTableVisible"
    >
      <template #default>
        <div
          v-if="dialogTableVisible && articleContent && articleContent.content"
          class="new-detail-item-cont"
          v-html="articleContent.content"
        ></div>
      </template>
      <template #footer>
        <el-button type="primary" @click="dialogTableVisible = false">
          {{ getLocaleText('关闭') }}
        </el-button>
      </template>
    </el-dialog>
  </span>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { getArticleDetails } from "@/api";
import useLocate from './uses/useLocate.js';
import LazyImage from "@/components/common/LazyImage/index.vue";
import PopoverPageRender from "@/components/render/PopoverPageRender.vue";
import { getSafeLink, isHttpLink, isTarget_Blank } from '@/utils/globalData.js';
import { useRenderConfig } from './uses/useRenderConfig.js';
import { resolveBindDataKeyValue } from '@/utils/renderBinding';

const { getLocaleText } = useLocate();

const props = defineProps({
  currentElementIndex: {
    type: Array,
    default: () => [],
  },
  idx: {
    type: Number,
    default: -1,
  },
  parentKeys: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: true,
  },
  config: {
    type: Object,
  },
});

const dialogTableVisible = ref(false);
const articleContent = ref(null);
const { bindData } = useRenderConfig(() => props.config);

const showSrc = computed(() => {
  return resolveBindDataKeyValue(
    bindData.value,
    props.config?.bindDataKey,
    props.config?.src || '',
  );
});

const hasMeaningfulClickConfig = (config) => {
  if (!config || !config.responseType) {
    return false;
  }
  if (config.responseType === 'link' || config.responseType === 'jumpLink') {
    return !!getSafeLink(config.link);
  }
  if (config.responseType === 'popup') {
    return !!config.article || !!config.popupTitle || !!config.popupClass;
  }
  if (config.responseType === 'popoverPage') {
    return (
      !!config.popoverPageId ||
      !!config.popoverPageTitle ||
      !!config.popoverPageData
    );
  }
  return true;
};

const activeInteractionConfig = computed(() => {
  return hasMeaningfulClickConfig(props.config?.clickConfig)
    ? props.config.clickConfig
    : null;
});

const isPopoverMode = computed(() => {
  return (
    activeInteractionConfig.value &&
    activeInteractionConfig.value.responseType === 'popoverPage' &&
    activeInteractionConfig.value.popoverPageData
  );
});

const popoverPlacement = computed(() => {
  return activeInteractionConfig.value?.popoverPlacement || 'right';
});

const popoverTrigger = computed(() => {
  return activeInteractionConfig.value?.popoverTrigger || 'hover';
});

const popoverClass = computed(() => {
  return ['image-item-popover', activeInteractionConfig.value?.popupClass || ''].filter(Boolean).join(' ');
});

const popoverStyle = computed(() => {
  return {
    padding: '0px',
           border:'none',
    borderRadius:'6px'
  };
});

const safeLink = computed(() => {
  const interactionConfig = activeInteractionConfig.value;
  if (interactionConfig) {
    if (!['link', 'jumpLink'].includes(interactionConfig.responseType || '')) {
      return '';
    }
    return getSafeLink(interactionConfig.link);
  }
  return getSafeLink(props.config?.link);
});

const jumpWay = computed(() => {
  const interactionConfig = activeInteractionConfig.value;
  if (interactionConfig) {
    return interactionConfig.responseType === 'jumpLink'
      ? isTarget_Blank()
      : '_Self';
  }
  return isHttpLink(props.config?.link) ? isTarget_Blank() : '_Self';
});

const getArticleContent = async () => {
  const interactionConfig = activeInteractionConfig.value;
  if (
    interactionConfig?.responseType !== 'popup' ||
    !interactionConfig?.article?.id
  ) {
    articleContent.value = null;
    return;
  }
  const param = {
    pageNo: 0,
    pageSize: 9999,
    id: interactionConfig.article.id,
    status: "1",
  };
  const res = await getArticleDetails(param);
  if (res.data.dataList) {
    articleContent.value = res.data.dataList[0];
    if (articleContent.value.type === 1 && articleContent.value.content) {
      articleContent.value.content = articleContent.value.content.replace(
        'video',
        'video autoplay '
      );
    }
  } else {
    articleContent.value = null;
  }
};

watch(
  () => activeInteractionConfig.value?.article,
  (value) => {
    if (value || activeInteractionConfig.value?.responseType === 'popup') {
      getArticleContent();
    }
  },
  { deep: true }
);

watch(
  () => activeInteractionConfig.value?.responseType,
  (value) => {
    if (value !== 'popup') {
      dialogTableVisible.value = false;
      articleContent.value = null;
      return;
    }
    getArticleContent();
  },
  { immediate: true }
);

onMounted(() => {
  getArticleContent();
});

const imageClickFn = (event) => {
  if (activeInteractionConfig.value?.responseType !== 'popup') {
    return;
  }
  event?.preventDefault?.();
  dialogTableVisible.value = true;
};
</script>

<style scoped>
.image-item-wrap {
  display: block;
  width: 100%;
  height: 100%;
}

.image-item-wrap--interactive {
  cursor: pointer;
}

.image-item {
  width: 100%;
  height: 100%;
  display: block;
}
</style>

<style>
.image-item-popover.el-popover {
  padding: 0 !important;
}
</style>
